/**
 * Appearance.
 *
 * `system` means no attribute at all, so the CSS media query decides; an
 * explicit choice stamps the root and wins in both directions. The address-bar
 * colour is kept in step, because on a phone that strip is part of the app.
 */
import type { Settings } from './settings';

type Theme = Settings['theme'];

const BAR_COLOUR: Record<'light' | 'dark', string> = {
  light: '#E6E9EC',
  dark: '#0B0F14',
};

function resolved(theme: Theme): 'light' | 'dark' {
  if (theme !== 'system') return theme;
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);

  // The two media-scoped `theme-color` tags in the markup cover the system
  // case; a manual choice needs one that overrides both.
  let override = document.querySelector<HTMLMetaElement>('meta[name="theme-color"][data-manual]');
  if (theme === 'system') {
    override?.remove();
    return;
  }
  if (!override) {
    override = document.createElement('meta');
    override.name = 'theme-color';
    override.dataset.manual = '';
    document.head.appendChild(override);
  }
  override.content = BAR_COLOUR[resolved(theme)];
}

/** Re-apply when the system flips, but only while following the system. */
export function watchSystemTheme(get: () => Theme): void {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (get() === 'system') applyTheme('system');
  });
}
